import { FC } from 'react'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import {
  Person as PersonIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import { useGetClientsQuery, useGetProjectsByClientQuery } from '../../redux/hooks.ts'
import type { ClientModel, ProjectModel } from '@itemize/server/models'

export interface ClientsTableProps {
  onView: (client: any) => void
  onEdit: (client: any) => void
  onDelete: (client: any) => void
  onToggleFavorite: (client: any) => void
  onToggleArchive: (client: any) => void
}

// Component to render a table section
const ClientTableSection: FC<{
  title: string
  clients: ClientModel[]
  onView: (client: ClientModel) => void
  onEdit: (client: ClientModel) => void
  onDelete: (client: ClientModel) => void
  onToggleFavorite: (client: ClientModel) => void
  onToggleArchive: (client: ClientModel) => void
  defaultExpanded?: boolean
}> = ({
  title,
  clients,
  onView,
  onEdit,
  onDelete,
  onToggleFavorite,
  onToggleArchive,
  defaultExpanded = true,
}) => {
  if (clients.length === 0) {
    return null
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        '&:before': { display: 'none' },
        boxShadow: 'none',
        '&:not(:last-child)': {
          borderBottom: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&.Mui-expanded': {
            minHeight: 48,
          },
          '& .MuiAccordionSummary-content': {
            '&.Mui-expanded': {
              margin: '12px 0',
            },
          },
        }}
      >
        <Typography variant="h6">
          {title} ({clients.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ display: 'none' }}>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client: ClientModel) => (
                <TableRow
                  key={client.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1">{client.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={0.5} justifyContent="flex-end">
                      <Tooltip
                        title={client.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <IconButton onClick={() => onToggleFavorite(client)} size="small">
                          {client.isFavorite ? (
                            <StarIcon sx={{ color: '#FFD700' }} />
                          ) : (
                            <StarBorderIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={client.isArchived ? 'Unarchive' : 'Archive'}>
                        <IconButton onClick={() => onToggleArchive(client)} size="small">
                          {client.isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View">
                        <IconButton onClick={() => onView(client)} size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => onEdit(client)} size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => onDelete(client)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  )
}

export const ClientsTable: FC<ClientsTableProps> = ({
  onView,
  onEdit,
  onDelete,
  onToggleFavorite,
  onToggleArchive,
}) => {
  const { data: clients, isLoading, error } = useGetClientsQuery()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Error loading clients: {String(error)}</Alert>
  }

  if (!clients || clients.length === 0) {
    return (
      <Paper elevation={2} sx={{ padding: 4, textAlign: 'center' }}>
        <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', marginBottom: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No clients found
        </Typography>
        <Typography color="text.secondary">Start by adding your first client.</Typography>
      </Paper>
    )
  }

  // Separate clients into categories
  const favoritedClients = clients.filter((client) => client.isFavorite && !client.isArchived)
  const normalClients = clients.filter((client) => !client.isFavorite && !client.isArchived)
  const archivedClients = clients.filter((client) => client.isArchived)

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        {clients.length} client{clients.length !== 1 ? 's' : ''} found
      </Typography>

      <Paper elevation={2} sx={{ marginTop: 2, backgroundColor: 'transparent' }}>
        <ClientTableSection
          title="Favorited Clients"
          clients={favoritedClients}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleFavorite={onToggleFavorite}
          onToggleArchive={onToggleArchive}
        />
      </Paper>
      <Paper elevation={2} sx={{ marginTop: 2, backgroundColor: 'transparent' }}>
        <ClientTableSection
          title="Clients"
          clients={normalClients}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleFavorite={onToggleFavorite}
          onToggleArchive={onToggleArchive}
        />
      </Paper>
      <Paper elevation={2} sx={{ marginTop: 2, backgroundColor: 'transparent' }}>
        <ClientTableSection
          title="Archived Clients"
          clients={archivedClients}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleFavorite={onToggleFavorite}
          onToggleArchive={onToggleArchive}
          defaultExpanded={false}
        />
      </Paper>
    </Box>
  )
}
